import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { FaWallet, FaInfoCircle, FaArrowLeft } from "react-icons/fa";
import CustomerLayout from "../components/layout/CustomerLayout";

export default function FundWallet() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [amount, setAmount] = useState("");
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");

  const walletBalance = parseFloat(user?.wallet_balance || 0);
  const SERVICE_CHARGE = 100;
  const quickAmounts = [1000, 2000, 5000, 10000, 20000, 50000];

  const handleProceed = () => {
    const num = parseInt(amount, 10);
    if (!num || num < 100) { setError("Minimum deposit is ₦100"); return; }
    if (num > 1000000) { setError("Maximum single deposit is ₦1,000,000"); return; }
    setError("");
    navigate("/wallet/fund/confirm", { state: { amount: num, walletBalance } });
  };

  return (
    <CustomerLayout
      title="Fund Wallet"
      subtitle="Add money to your UCH wallet"
      action={
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#0d3d2e] transition">
          <FaArrowLeft className="w-3 h-3" /> Back
        </button>
      }
    >
      <div className="max-w-lg mx-auto space-y-5">
        {/* Balance */}
        <div className="bg-gradient-to-br from-[#0d3d2e] to-[#0f4a38] rounded-2xl p-6 text-white">
          <p className="text-green-300 text-xs font-semibold uppercase tracking-wide mb-1">Current Balance</p>
          <p className="text-4xl font-black">₦{walletBalance.toLocaleString("en-NG", { minimumFractionDigits: 2 })}</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-5">
          {/* Quick amounts */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Quick Select</p>
            <div className="grid grid-cols-3 gap-2.5">
              {quickAmounts.map(a => (
                <button key={a} onClick={() => { setAmount(String(a)); setSelected(a); setError(""); }}
                  className={`py-3 rounded-xl text-sm font-bold transition border-2 ${selected === a ? "border-[#0d3d2e] bg-green-50 text-[#0d3d2e]" : "border-gray-100 bg-gray-50 text-gray-700 hover:border-green-300"}`}>
                  ₦{a.toLocaleString()}
                </button>
              ))}
            </div>
          </div>

          {/* Custom amount */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Or enter amount</p>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold">₦</span>
              <input type="number" value={amount} min="100" max="1000000"
                onChange={e => { setAmount(e.target.value); setSelected(null); setError(""); }}
                placeholder="Enter amount"
                className="w-full pl-8 pr-4 py-3.5 border border-gray-200 rounded-xl text-base font-semibold focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
            </div>
          </div>

          {/* Fee breakdown */}
          {amount && parseInt(amount) >= 100 && (
            <div className="bg-[#f5f5f0] rounded-xl px-4 py-3 space-y-2 text-sm">
              {[
                ["Deposit amount", `₦${parseInt(amount || 0).toLocaleString()}`, "text-gray-900"],
                ["Service charge", `₦${SERVICE_CHARGE}`, "text-orange-600"],
                ["Total debit", `₦${(parseInt(amount || 0) + SERVICE_CHARGE).toLocaleString()}`, "text-[#0d3d2e] font-bold"],
                ["Credited to wallet", `₦${parseInt(amount || 0).toLocaleString()}`, "text-green-700"],
              ].map(([k, v, cls]) => (
                <div key={k} className="flex justify-between">
                  <span className="text-gray-500">{k}</span>
                  <span className={`font-semibold ${cls}`}>{v}</span>
                </div>
              ))}
            </div>
          )}

          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{error}</p>}
        </div>

        <div className="flex items-start gap-2.5 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-sm text-blue-700">
          <FaInfoCircle className="shrink-0 mt-0.5 w-4 h-4" />
          <p>A ₦{SERVICE_CHARGE} service charge applies to all deposits. The deposited amount is credited to your wallet in full.</p>
        </div>

        <button onClick={handleProceed} disabled={!amount || parseInt(amount) < 100}
          className="w-full bg-[#0d3d2e] hover:bg-[#0a3226] text-white font-bold py-4 rounded-2xl transition disabled:opacity-50 disabled:cursor-not-allowed text-base">
          Continue to Payment
        </button>
      </div>
    </CustomerLayout>
  );
}
